import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthState } from '../../lib/partnerAuth';
import { useSEO } from '../../hooks/useSEO';
import {
  getPayoutDisplayStatus,
  getPayoutEligibility,
  getUnpaidBalance,
  MIN_PAYOUT_USD,
  PAYOUT_DISPLAY_LABELS,
} from '../../lib/partnerStatsFilters';
import styles from './Earnings.module.css';

interface Payout {
  month: string;
  totalRevenue?: number;
  totalCommission?: number;
  totalInstalls?: number;
  totalEarnings?: number;
  paidAmount?: number;
  paidAt?: unknown;
  status?: string;
}

interface PartnerTotals {
  totalEarnings: number;
  totalPaid: number;
}

function normalizePayout(month: string, raw: Partial<Payout>): Payout {
  return {
    month,
    totalRevenue: raw.totalRevenue,
    totalCommission: raw.totalCommission,
    totalInstalls: raw.totalInstalls,
    totalEarnings: raw.totalEarnings ?? raw.totalCommission ?? 0,
    paidAmount: raw.paidAmount ?? 0,
    paidAt: raw.paidAt ?? null,
    status: raw.status ?? 'pending',
  };
}

function badgeClassForStatus(status: ReturnType<typeof getPayoutDisplayStatus>): string {
  if (status === 'paid') return styles.paid;
  if (status === 'pending_payout') return styles.pendingPayout;
  return styles.accumulating;
}

export function PartnerEarnings() {
  const { user } = useAuthState();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [partnerTotals, setPartnerTotals] = useState<PartnerTotals | null>(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Earnings — LuvLust Partners',
    description: 'View your monthly earnings and payment history.',
  });

  useEffect(() => {
    if (!user) return;
    async function load() {
      const partnerSnap = await getDocs(
        query(collection(db, 'partners'), where('uid', '==', user!.uid), limit(1)),
      );
      if (partnerSnap.empty) {
        setLoading(false);
        return;
      }
      const partnerId = partnerSnap.docs[0].id;
      const partnerData = partnerSnap.docs[0].data() as PartnerTotals;
      setPartnerTotals({
        totalEarnings: partnerData.totalEarnings ?? 0,
        totalPaid: partnerData.totalPaid ?? 0,
      });

      const payoutSnap = await getDocs(collection(db, `partners/${partnerId}/payouts`));
      const data = payoutSnap.docs
        .map((d) => normalizePayout(d.id, d.data() as Partial<Payout>))
        .sort((a, b) => b.month.localeCompare(a.month));
      setPayouts(data);
      setLoading(false);
    }
    load();
  }, [user]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
        Loading earnings...
      </div>
    );
  }

  const unpaid = partnerTotals
    ? getUnpaidBalance(partnerTotals.totalEarnings, partnerTotals.totalPaid)
    : 0;
  const eligibility = getPayoutEligibility(unpaid);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Monthly Earnings</h1>

      {partnerTotals ? (
        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Unpaid balance</span>
            <span className={styles.summaryValue}>${unpaid.toFixed(2)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Minimum payout</span>
            <span className={styles.summaryValue}>${MIN_PAYOUT_USD.toFixed(2)}</span>
          </div>
          <div className={styles.summaryStatus}>
            {eligibility === 'eligible' ? (
              <>
                <span className={styles.statusEligible}>Eligible for payout</span>
                <p className={styles.summaryNote}>
                  We process payouts monthly. Status will change to Paid once sent.
                </p>
              </>
            ) : eligibility === 'accumulating' ? (
              <>
                <span className={styles.statusAccumulating}>Building toward $10 minimum</span>
                <p className={styles.summaryNote}>
                  Payouts are sent once your unpaid balance reaches ${MIN_PAYOUT_USD}. Your
                  earnings roll over until then.
                </p>
              </>
            ) : (
              <p className={styles.summaryNote}>
                Share your referral link to start earning $0.10 per verified install.
              </p>
            )}
          </div>
        </div>
      ) : null}

      {payouts.length === 0 ? (
        <div className={styles.empty}>
          No earnings yet. Share your referral link to start earning!
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Installs</th>
              <th>Earnings</th>
              <th>Paid Amount</th>
              <th>Payout status</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((p) => {
              const displayStatus = getPayoutDisplayStatus(p.status, unpaid);
              return (
                <tr key={p.month}>
                  <td className={styles.month}>{p.month}</td>
                  <td>{p.totalInstalls ?? '-'}</td>
                  <td className={styles.commission}>${(p.totalEarnings ?? 0).toFixed(2)}</td>
                  <td>${(p.paidAmount ?? 0).toFixed(2)}</td>
                  <td>
                    <span
                      className={`${styles.badge} ${badgeClassForStatus(displayStatus)}`}
                      title={
                        displayStatus === 'accumulating'
                          ? "This month's earnings count toward your $10 minimum"
                          : undefined
                      }
                    >
                      {PAYOUT_DISPLAY_LABELS[displayStatus]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
